import React, { useRef, useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { setHours, setMinutes, setSeconds, format, subDays } from 'date-fns';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { includes, isArray, isEmpty } from 'lodash';
import axios from 'axios';

import { Table } from '~/components';
import {
  DateTime,
  Autocomplete,
  Input,
  RadioGroup,
  Hidden,
} from '~/components/Form';
import { GFO, GFU } from '~/constants/perfil';
import { t, number, parseDateString } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function Reldadosabastecimentoveiculo() {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [supplies, setSupplies] = useState([]);
  const [items, setItems] = useState([]);
  const [temp, setTemp] = useState({});

  const formRef = useRef(null);

  const {
    glousuariotipo,
    glousuariologin,
    glousuarioorgacodigo,
    glousuarioorganome,
  } = useSelector((state) => state.user.profile);
  const { token } = useSelector((state) => state.auth);

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(subDays(new Date(), 7), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const validation = useMemo(
    () => ({
      datahorainicial: Yup.date()
        .typeError(t('s.geral.validacao.campoobrigatorio'))
        .transform((_, value) => parseDateString(value))
        .required(t('s.geral.validacao.campoobrigatorio')),
      datahorafinal: Yup.date()
        .typeError(t('s.geral.validacao.campoobrigatorio'))
        .transform((_, value) => parseDateString(value))
        .required(t('s.geral.validacao.campoobrigatorio'))
        .max(datahorafinal, `A data não pode ser superior a ${datahorafinal}`)
        .min(
          Yup.ref('datahorainicial'),
          'A data não pode ser inferior a Data inicial'
        ),
      veic_placa: Yup.string().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
    }),
    [datahorafinal]
  );

  const header = useMemo(
    () => [
      { id: 'dataInicial', label: 'Data' },
      { id: 'horaInicial', label: 'Início' },
      { id: 'horaFinal', label: 'Fim' },
      { id: 'poco_desc_abrev', label: 'Posto' },
      { id: 'orga_nome', label: 'Órgão' },
      { id: 'unid_nome', label: 'Unidade' },
      { id: 'veic_placa', label: 'Veículo' },
      { id: 'movi_totalizador_veiculo', label: 'Hodômetro', align: 'right' },
      { id: 'prod_display', label: 'Produto' },
      {
        id: 'movi_totalizador_veic_anterior',
        label: 'Encerr. Inicial',
        format: (value) => number(value, 1),
        align: 'right',
      },
      {
        id: 'movi_totalizador_veiculo',
        label: 'Encerr. Final',
        format: (value) => number(value, 1),
        align: 'right',
      },
      {
        id: 'movi_volume',
        label: 'Volume',
        align: 'right',
        format: (value) => number(value, 1),
      },
      { id: 'cond_cpf', label: 'Condutor' },
      { id: 'movi_status', label: 'Status' },
    ],
    []
  );

  const initials = useMemo(
    () => ({
      datahorainicial,
      datahorafinal,
      formato: 'pdf',
    }),
    [datahorainicial, datahorafinal]
  );

  const handleSubmit = useCallback(
    async (data) => {
      try {
        setLoading(true);

        formRef.current.setErrors({});

        const schema = Yup.object().shape(validation);

        await schema.validate(data, {
          abortEarly: false,
        });

        if (isEmpty(items))
          throw 'Selecione ao menos um abastecimento antes de enviar.';

        const { datahorainicial, datahorafinal, formato } = data;
        const { REACT_APP_URL_DOWNLOAD } = process.env;

        const formData = {
          datahorainicial,
          datahorafinal,
          tipo: formato,
          usuario: glousuariologin,
          cod_orgao: items[0].orga_codigo,
          des_orgao: items[0].orga_nome,
          cod_unidade: items[0].unid_codigo,
          des_unidade: items[0].unid_nome,
          placa_veiculo: items[0].veic_placa,
          data_abastecimento: items[0].dataInicial,
          hora_inicio: items[0].horaInicial,
          hora_fim: items[0].horaFinal,
          placa_marca_modelo: items[0].vemm_nome,
          condutor: `${items[0].cond_cpf} ${items[0].cond_nome}`,
          posto: items[0].poco_desc_abrev,
          municipio: items[0].muni_nome,
          frentista: items[0].usua_nome_frentista,
          combustivel: items[0].prod_display,
          volume: number(items[0].movi_volume, 1),
          hodometro_anterior: number(
            items[0].movi_totalizador_veic_anterior,
            1
          ),
          hodometro_atual: number(items[0].movi_totalizador_veiculo, 1),
          km_percorrida: number(items[0].movi_totalizador_rodado, 1),
          consumo_kml: number(items[0].movi_desempenho, 1),
          preco_unitario: 'R$ ' + number(items[0].movi_preco_unitario, 4),
          valor_total_abastecimento:
            'R$ ' + number(items[0].movi_preco_total, 2),
          status: items[0].movi_status,
          abastecimento_contigencia: items[0].movi_contigencia_str,
          gestor_aprovacao: items[0].usua_login_gestor_liberacao
            ? items[0].usua_login_gestor_liberacao
            : '',
        };

        const url = new URL(REACT_APP_URL_DOWNLOAD);

        url.search = new URLSearchParams({
          relatorio: 'dadosabastecimentoveiculo',
          token,
          ...formData,
        });

        const response = await axios({
          url: url.href,
          method: 'GET',
          responseType: 'blob',
        });

        window.open(response.config.url)

        // const file = new Blob([response.data], { type: response.data.type });

        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(file);
        // } else {
        //   const objectUrl = URL.createObjectURL(file);
        //   window.open(objectUrl);
        // }
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message;
          });

          formRef.current.setErrors(validationErrors);
        } else {
          if (isArray(err)) err.map((e) => toast.error(t(e)));
          else toast.error(t(err));
        }
      } finally {
        setLoading(false);
      }
    },
    [items, datahorafinal]
  );

  const handleGetAbastecimentos = useCallback(async () => {
    try {
      setLoading(true);

      formRef.current.setErrors({});

      const formData = formRef.current.getData();

      const schema = Yup.object().shape(validation);

      await schema.validate(formData, {
        abortEarly: false,
      });

      const { data } = await api.get('abastecimento/periodovolume', {
        params: {
          periodoInicio: formData.datahorainicial,
          periodoFim: formData.datahorafinal,
          orgaos: formData.orga_codigo, // vazio
          placa: formData.veic_placa,
          prod_codigo: formData.prod_codigo ?? 0,
        },
      });

      setSupplies(data);
    } catch (err) {
      setSupplies([]);

      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });

        formRef.current.setErrors(validationErrors);
      } else {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    } finally {
      setLoading(false);
    }
  }, [validation, datahorafinal]);

  const handleClearSupplies = useCallback(() => {
    setSupplies([]);
    setItems([]);
  }, []);

  const handleLookForDifference = useCallback(
    (field, value) => {
      if (temp[field] !== value) handleClearSupplies();
    },
    [temp]
  );

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth
      open={open}
      onClise={() => setOpen(false)}
      maxWidth="sm"
    >
      <DialogTitle>Dados do Abastecimento do Veículo</DialogTitle>
      <DialogContent>
        <S.Wrapper ref={formRef} onSubmit={handleSubmit} initialData={initials}>
          <DateTime
            time
            disableFuture
            label="Data Inicial"
            name="datahorainicial"
            onBlur={(value) =>
              handleLookForDifference('start', value.target.value)
            }
            onAccept={handleClearSupplies}
            getValue={(value) =>
              setTemp((state) => ({ ...state, start: value }))
            }
          />

          <DateTime
            time
            disableFuture
            label="Data Final"
            name="datahorafinal"
            onBlur={(value) =>
              handleLookForDifference('end', value.target.value)
            }
            onAccept={handleClearSupplies}
            getValue={(value) => setTemp((state) => ({ ...state, end: value }))}
            maxDateMessage=""
          />

          {(glousuariotipo === GFO || glousuariotipo === GFU) && (
            <>
              <Input
                name="orga_nome"
                value={glousuarioorganome}
                label="Órgão"
                disabled
              />

              <Hidden
                name="orga_codigo"
                value={glousuarioorgacodigo}
                label="Órgão"
                disabled
              />
            </>
          )}

          {glousuariotipo !== GFO && glousuariotipo !== GFU && (
            <Autocomplete
              name="orga_codigo"
              label="orga_nome"
              title="Órgão"
              url="orgaos"
              unique="orga_codigo"
              disabled={includes([GFO, GFU], glousuariotipo)}
              onInputChange={handleClearSupplies}
            />
          )}

          <Autocomplete
            name="prod_codigo"
            label="prod_descricao"
            title="Produto"
            url="produtos"
            unique="prod_codigo"
            onInputChange={handleClearSupplies}
          />

          <Input
            name="veic_placa"
            label="Placa"
            getValue={handleClearSupplies}
          />

          <Button
            type="button"
            startIcon={<Send />}
            color="default"
            variant="outlined"
            onClick={handleGetAbastecimentos}
          >
            Buscar Abastecimentos
          </Button>

          <Table
            radio
            noButton
            cells={header}
            data={supplies}
            loading={loading}
            onCallback={(value) => setItems(value)}
            size="small"
            title="Abastecimentos"
          />

          <RadioGroup
            row
            name="formato"
            label="Formato"
            options={[
              { label: 'PDF', value: 'pdf' },
              { label: 'Excel', value: 'xls' },
            ]}
            defaultValue="pdf"
            serialize={(value) => value}
          />
        </S.Wrapper>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => formRef.current.submitForm()}
          disabled={loading}
        >
          Exibir
        </Button>

        <Button
          color="secondary"
          variant="outlined"
          onClick={() => setOpen(false)}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
